import { useState, useEffect, useRef } from "react";
import useSupportStore from "../../stores/useSupportStore";
import SupportDrawer from "./SupportDrawer";
import FeedbackOverlay from "./FeedbackOverlay";
import "./Support.css";

// ── Recording Indicator (top-right bar while recording) ──
function RecordingIndicator({ startTime, onStop }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - startTime) / 1000)), 1000);
    return () => clearInterval(id);
  }, [startTime]);

  const mins = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const secs = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="support-recording-bar" onClick={onStop}>
      <span className="support-recording-dot" />
      <span className="support-recording-label">Recording issue</span>
      <span className="support-recording-time">{mins}:{secs}</span>
      <span className="support-recording-stop">Stop & Report</span>
    </div>
  );
}

const SupportButton = () => {
  const { drawerOpen, openDrawer, unreadCount, loadTickets, updateFromServer, isRecording, recordingStartTime, startRecording, stopRecording } = useSupportStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!window.electronAPI?.support) return;
    loadTickets();
    const unsub = window.electronAPI.support.onTicketUpdate(() => {
      updateFromServer();
    });
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, []);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  if (!window.electronAPI?.support) return null;

  const handleReportIssue = () => {
    setMenuOpen(false);
    setOverlayOpen(true);
  };

  const handleRecordIssue = () => {
    setMenuOpen(false);
    startRecording();
  };

  const handleStopRecording = () => {
    stopRecording();
    // Open feedback overlay after stopping — recordingData is now in the store
    setOverlayOpen(true);
  };

  const handleMyTickets = () => {
    setMenuOpen(false);
    openDrawer();
  };

  return (
    <>
      {/* Recording indicator bar */}
      {isRecording && recordingStartTime && (
        <RecordingIndicator startTime={recordingStartTime} onStop={handleStopRecording} />
      )}

      <div ref={menuRef} style={{ position: "fixed", bottom: 20, right: 20, zIndex: 99999 }}>
        {/* Popover menu */}
        {menuOpen && (
          <div className="support-popover">
            <button className="support-popover-btn primary" onClick={handleReportIssue}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
              </svg>
              Report Issue
            </button>
            <button className="support-popover-btn record" onClick={isRecording ? handleStopRecording : handleRecordIssue}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                {isRecording ? (
                  <rect x="9" y="9" width="6" height="6" fill="currentColor" stroke="none" />
                ) : (
                  <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />
                )}
              </svg>
              {isRecording ? "Stop Recording" : "Record Issue"}
            </button>
            <button className="support-popover-btn" onClick={handleMyTickets}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              My Tickets
              {unreadCount > 0 && <span className="support-popover-badge">{unreadCount}</span>}
            </button>
          </div>
        )}

        {/* Help button */}
        <button
          className={`support-help-btn${isRecording ? " recording" : ""}`}
          onClick={() => setMenuOpen(!menuOpen)}
          title="Support"
        >
          {isRecording ? (
            <span className="support-help-rec-dot" />
          ) : (
            "?"
          )}
          {unreadCount > 0 && <span className="support-help-badge">{unreadCount}</span>}
        </button>
      </div>

      {drawerOpen && <SupportDrawer />}
      <FeedbackOverlay open={overlayOpen} onClose={() => setOverlayOpen(false)} />
    </>
  );
};

export default SupportButton;
